// ─────────────────────────────────────────────────────────────────────────────
// Session bootstrap for the game client.
//
// The backend rejects an unauthenticated WebSocket upgrade, so a session must
// exist *before* connecting. Visitors who land straight on a game URL without
// passing through the landing page get a guest session minted here.
//
// The etoo_session cookie itself is HttpOnly and unreadable from JavaScript;
// this only asks the auth service who we are.
//
// Duplicated per game frontend, which each have their own build.
// ─────────────────────────────────────────────────────────────────────────────

export type SessionKind = 'user' | 'guest';

export interface SessionInfo {
  kind: SessionKind;
  name: string | null;
  needsUsername?: boolean;
}

interface ApiErrorBody {
  message?: string;
}

export class SessionApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'SessionApiError';
  }
}

async function getJson<T>(path: string, init?: RequestInit): Promise<T | null> {
  try {
    const response = await fetch(`/api${path}`, { credentials: 'same-origin', ...init });
    if (!response.ok) return null;
    return (await response.json()) as T;
  } catch {
    return null;
  }
}

/**
 * Resolves the current identity, minting a guest session if there is none.
 * Returns null only when the auth service is unreachable, in which case the
 * caller should surface an error rather than connecting.
 */
export async function ensureSession(): Promise<SessionInfo | null> {
  const existing = await getJson<SessionInfo>('/session');
  if (existing && existing.name) return existing;

  // A signed-in account with no username yet cannot be seated: the mandatory
  // picker lives on the landing page.
  if (existing && existing.needsUsername) return existing;

  return getJson<SessionInfo>('/session/guest', { method: 'POST' });
}

export async function renameGuest(name: string): Promise<SessionInfo> {
  const response = await fetch('/api/session/guest/name', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  });
  const body = (await response.json().catch(() => null)) as
    | (SessionInfo & ApiErrorBody)
    | null;
  if (!response.ok) {
    throw new SessionApiError(
      response.status,
      body?.message ?? `Could not change name (${response.status})`,
    );
  }
  if (!body || body.kind !== 'guest' || !body.name) {
    throw new SessionApiError(500, 'The server returned an invalid guest session');
  }
  return body;
}

/** Where to send a player who wants to change their name or sign in. */
export const ACCOUNT_URL = '/';
